import { Box, Button } from "@mui/material";
import { useState } from "react";

/**
 * Shared footer actions for entity edit/create side panels (the pattern first established
 * by the Records edit panel): the submit and destructive actions sit in a single row, and
 * deleting swaps that row for an inline Cancel / Confirm pair rather than a separate dialog.
 *
 * Render this inside the panel's `<form onSubmit=...>` — the submit button is `type="submit"`.
 */
export function EditPanelActions({
  isEditing,
  submitLabel,
  deleteLabel = "Delete",
  confirmDeleteLabel = "Confirm delete",
  cancelLabel = "Cancel",
  onDelete,
  saving = false,
  deleting = false,
  submitDisabled = false,
}: {
  /** True when editing an existing entity (shows the delete affordance). */
  isEditing: boolean;
  submitLabel: string;
  deleteLabel?: string;
  confirmDeleteLabel?: string;
  cancelLabel?: string;
  /** Triggers the delete; omit to hide the delete affordance entirely. */
  onDelete?: () => void;
  saving?: boolean;
  deleting?: boolean;
  /** Disables submit for validation or a pristine form without hiding the action. */
  submitDisabled?: boolean;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canDelete = isEditing && Boolean(onDelete);

  return (
    <Box className="records-modal-actions side-panel-actions">
      {confirmDelete ? (
        <>
          <Button variant="outlined" type="button" disabled={deleting} onClick={() => setConfirmDelete(false)}>
            {cancelLabel}
          </Button>
          <Button variant="outlined" color="error" type="button" disabled={deleting} onClick={onDelete}>
            {confirmDeleteLabel}
          </Button>
        </>
      ) : (
        <>
          {canDelete ? (
            <Button variant="outlined" color="error" type="button" onClick={() => setConfirmDelete(true)}>
              {deleteLabel}
            </Button>
          ) : null}
          <Button variant="contained" type="submit" disabled={saving || submitDisabled}>
            {submitLabel}
          </Button>
        </>
      )}
    </Box>
  );
}
